import React, { useEffect, useState } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  CheckboxGroup,
  Checkbox,
  Select,
  SelectItem,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import addUserImgModal from "../../../adduser-modal.png";

type FormValues = {
  user_name: string;
  user_given_name: string;
  availability: string;
  allow_update_entities: string;
  allow_update_assignees: string;
  user_email: string;
  entities: string[];
  user_group: string[];
  organization_name: string;
};

type EntityType = {
  entity_id: string;
  entity_name: string;
  entity_address: string;
  entity_postalCode: string;
  entity_city: string;
  entity_email: string[];
};

export default function AddUserModal({ isOpen, onClose, classNames }) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    control,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      user_name: "",
      user_given_name: "",
      availability: "active",
      user_email: "",
      entities: [],
      user_group: [],
      organization_name: "",
    },
  });

  const [isEntitiesInvalid, setIsEntitiesInvalid] = useState(true);
  const [isUserGroupInvalid, setIsUserGroupInvalid] = useState(true);
  const [dataEntities, setDataEntities] = useState<EntityType[]>([]);
  const [dataOrganizationName, setDataOrganizationName] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchENtities = async () => {
      try {
        const responseUseCases = await axiosPrivate.get(`/useCases`);
        const entitiesData = responseUseCases.data["data"][0]["settings"]["entities"];
        const organizationNameData =
          responseUseCases.data["data"][0]["settings"]["organization"]["name"];
        setDataEntities(entitiesData);
        setDataOrganizationName(organizationNameData);
      } catch (error) {
        console.error("Erreur lors de la récupération des entities:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchENtities();
  }, [axiosPrivate]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (data.entities.length === 0) {
      setError("entities", {
        type: "manual",
        message: t("select_at_least_one_entity"),
      });
      return;
    } else {
      clearErrors("entities");
    }

    if (data.user_group.length === 0) {
      setError("user_group", {
        type: "manual",
        message: t("select_at_least_one_user_profile"),
      });
      return;
    } else {
      clearErrors("user_group");
    }

    const formattedData = {
      ...data,
      allow_update_entities: data.allow_update_entities === "true",
      allow_update_assignees: data.allow_update_assignees === "true",
      entities: Array.isArray(data.entities) ? data.entities.join(",") : data.entities,
      user_group: Array.isArray(data.user_group) ? data.user_group.join(",") : data.user_group,
      organization_name: dataOrganizationName,
    };

    await axiosPrivate.post(`/users`, formattedData);

    reset();
    onClose();
    queryClient.invalidateQueries({ queryKey: ["AllUsers"] });
  };

  if (loading) {
    return <div>{t("loading")}</div>;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      placement="top-center"
      size="2xl"
      classNames={{
        backdrop: "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
      }}
    >
      <ModalContent
        as="form"
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        <ModalBody>
          <div className="flex pt-5">
            <div>
              <div className="text-2xl font-bold pt-3 pb-6">{t("add_user")}</div>
              <div className="text-vistagreen">{t("fill_required_info_for_account_creation")}</div>
            </div>
            <img
              src={addUserImgModal}
              alt={t("alt_vistaflow_user")}
              className="w-48 origin-left self-center"
            />
          </div>

          <Controller
            name="user_name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label={t("name")}
                placeholder={t("name")}
                variant="flat"
                labelPlacement="outside"
                isRequired
                classNames={classNames}
              />
            )}
          />

          <Controller
            name="user_given_name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label={t("first_name")}
                placeholder={t("first_name")}
                variant="flat"
                labelPlacement="outside"
                isRequired
                classNames={classNames}
              />
            )}
          />

          <Controller
            name="availability"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label={t("status")}
                isRequired
                classNames={classNames}
                defaultSelectedKeys={["active"]}
                labelPlacement="outside"
                disableSelectorIconRotation
                disallowEmptySelection
                onChange={(value) => field.onChange(value)}
              >
                <SelectItem key="active">{t("active")}</SelectItem>
                <SelectItem key="inactive">{t("inactive")}</SelectItem>
              </Select>
            )}
          />

<Controller
  name="allow_update_entities"
  control={control}
  defaultValue="false" // Convertir en chaîne
  render={({ field }) => (
    <Select
      {...field}
      label={t("allow_update_entities")}
      classNames={classNames}
      defaultSelectedKeys={[field.value]} // Maintenant c'est une chaîne
      labelPlacement="outside"
      disableSelectorIconRotation
      disallowEmptySelection
      onChange={(value) => field.onChange(value)} // Pas besoin de conversion ici
    >
      <SelectItem key="true">{t("authorized")}</SelectItem>
      <SelectItem key="false">{t("unauthorized")}</SelectItem>
    </Select>
  )}
/>


<Controller
  name="allow_update_assignees"
  control={control}
  defaultValue="false" // Convertir en chaîne
  render={({ field }) => (
    <Select
      {...field}
      label={t("allow_update_assignees")}
      classNames={classNames}
      defaultSelectedKeys={[field.value]} // Maintenant c'est une chaîne
      labelPlacement="outside"
      disableSelectorIconRotation
      disallowEmptySelection
      onChange={(value) => field.onChange(value)} // Pas besoin de conversion ici
    >
      <SelectItem key="true">{t("authorized")}</SelectItem>
      <SelectItem key="false">{t("unauthorized")}</SelectItem>
    </Select>
  )}
/>
         

          <Controller
            name="user_email"
            control={control}
            rules={{
              validate: (value) => {
                if (/\s/.test(value)) {
                  return t("email_no_spaces");
                }
                return true;
              }
            }}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label={t("email")}
                placeholder={t("email")}
                variant="flat"
                labelPlacement="outside"
                isRequired
                isInvalid={!!errors.user_email}
                errorMessage={errors.user_email?.message}
                classNames={classNames}
              />
            )}
          />

          <Controller
            name="entities"
            control={control}
            render={({ field }) => (
              <CheckboxGroup
                {...field}
                label={t("entity_visibility")}
                isRequired
                isInvalid={errors.entities ? true : isEntitiesInvalid}
                onChange={(value: string[] | any) => {
                  field.onChange(value);
                  setIsEntitiesInvalid(value.length < 1);
                }}
                classNames={classNames}
                color="default"
                size="sm"
              >
                {dataEntities.map((entity) => (
                  <Checkbox key={entity.entity_id} value={entity.entity_id}>
                    {entity.entity_name}
                  </Checkbox>
                ))}
              </CheckboxGroup>
            )}
          />

          <Controller
            name="user_group"
            control={control}
            render={({ field }) => (
              <CheckboxGroup
                {...field}
                label={t("user_profile")}
                isRequired
                isInvalid={errors.user_group ? true : isUserGroupInvalid}
                onChange={(value: string[] | any) => {
                  field.onChange(value);
                  setIsUserGroupInvalid(value.length < 1);
                }}
                classNames={classNames}
                color="default"
                size="sm"
              >
                <Checkbox value="Admins">{t("profil_administrator")}</Checkbox>
                <Checkbox value="DataEntrySupervisors">
                  {t("profil_data_entry_supervisors")}
                </Checkbox>
                <Checkbox value="DataEntryOperators">{t("profil_data_entry_operator")}</Checkbox>
                <Checkbox value="DataReadOnlyOperators">
                  {t("profil_data_read_only_operators")}
                </Checkbox>
              </CheckboxGroup>
            )}
          />

          {errors.entities && (
            <p className="bg-red-500 text-sm text text-white p-1 text-center rounded-md">
              {errors.entities.message}
            </p>
          )}
          {errors.user_group && (
            <p className="bg-red-500 text-sm text text-white p-1 text-center rounded-md">
              {errors.user_group.message}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="flat" onPress={onClose}>
            {t("cancel")}
          </Button>
          <Button type="submit" disabled={!isValid}>
            {t("add_user")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
